import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import SentryHealthTile from "@/components/SentryHealthTile";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils-app";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { ArrowsClockwiseIcon as ArrowsClockwise, WarningIcon as Warning, InfoIcon as Info, CheckCircleIcon as CheckCircle, ArrowRightIcon as ArrowRight, MagnifyingGlassIcon as MagnifyingGlass, LightningIcon as Lightning, EnvelopeSimpleIcon as EnvelopeSimple, GitCommitIcon as GitCommit, CloudArrowUpIcon as CloudArrowUp, SparkleIcon as Sparkle } from "@phosphor-icons/react";
import { ADMIN_WHATS_NEW } from "@/data/adminWhatsNew";

const SEVERITY_STYLES = {
  critical: {
    border: "border-[#C1272D]/40",
    bg: "bg-[#C1272D]/5",
    badge: "bg-[#C1272D] text-white",
    text: "text-[#C1272D]",
    icon: Warning,
  },
  warn: {
    border: "border-[#8A6A00]/40",
    bg: "bg-[#FFF8E6]",
    badge: "bg-[#8A6A00] text-white",
    text: "text-[#8A6A00]",
    icon: Warning,
  },
  info: {
    border: "border-[#E5E5E5]",
    bg: "bg-white",
    badge: "bg-[#0E0E0E]/10 text-[#0E0E0E]",
    text: "text-[#0E0E0E]",
    icon: Info,
  },
  ok: {
    border: "border-[#E5E5E5]",
    bg: "bg-white",
    badge: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    text: "text-emerald-700",
    icon: CheckCircle,
  },
};

export default function AdminOperations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [digestOptedIn, setDigestOptedIn] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [deploy, setDeploy] = useState(null);
  const [markingDeployed, setMarkingDeployed] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ops/summary");
      setData(data);
      setLoadError(null);
    } catch (err) {
      setLoadError(err?.response?.data?.detail || err?.message || "Failed to load operations summary.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    load();
    // Fetch digest opt-in state once on mount. Failures are silent —
    // the toggle just won't render if the call 401s.
    api.get("/admin/automations/digest-opt-in")
      .then((r) => setDigestOptedIn(!!r.data.opted_in))
      .catch(() => setDigestOptedIn(null));
    // Fetch deploy status so we can show "X commits pending redeploy".
    api.get("/admin/ops/deploy-status", { params: { limit: 25 } })
      .then((r) => setDeploy(r.data))
      .catch(() => setDeploy(null));
    const id = setInterval(load, 5 * 60 * 1000); // auto-refresh every 5 min
    return () => clearInterval(id);
  }, [isAdmin, load]);

  const reloadDeploy = () => {
    api.get("/admin/ops/deploy-status", { params: { limit: 25 } })
      .then((r) => setDeploy(r.data))
      .catch(() => {});
  };

  const markDeployed = async () => {
    setMarkingDeployed(true);
    try {
      await api.post("/admin/ops/mark-deployed");
      toast.success("Marked. Operations Hub will now count only commits made after this moment.");
      reloadDeploy();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record deploy marker.");
    } finally {
      setMarkingDeployed(false);
    }
  };

  const toggleDigest = async () => {
    setDigestBusy(true);
    try {
      const next = !digestOptedIn;
      const { data } = await api.put("/admin/automations/digest-opt-in", { opted_in: next });
      setDigestOptedIn(!!data.opted_in);
      toast.success(
        data.opted_in
          ? "Subscribed — you'll get the morning digest at 08:00 UTC."
          : "Unsubscribed from the morning digest.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update digest preference.");
    } finally {
      setDigestBusy(false);
    }
  };

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  const refresh = () => {
    setRefreshing(true);
    load();
  };

  const cards = data?.cards || [];
  const criticalCount = cards.filter((c) => c.severity === "critical").length;
  const warnCount = cards.filter((c) => c.severity === "warn").length;
  const allClear = !loading && criticalCount === 0 && warnCount === 0;

  return (
    <AppShell>
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
            Admin
          </p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">
            Operations
          </h1>
          <p className="text-[#6B6B6B] mt-3 max-w-2xl">
            Everything you should check daily to keep the registry healthy.
            Auto-refreshes every 5 minutes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:justify-end">
          {digestOptedIn !== null && (
            <Button
              variant={digestOptedIn ? "default" : "outline"}
              onClick={toggleDigest}
              disabled={digestBusy}
              className={`rounded-full ${digestOptedIn ? "bg-[#0E0E0E] hover:bg-[#C1272D] text-white" : ""}`}
              data-testid="ops-digest-toggle"
              title={
                digestOptedIn
                  ? "You'll get the morning ops summary by email at 08:00 UTC. Click to unsubscribe."
                  : "Get a daily summary of this page in your inbox at 08:00 UTC."
              }
            >
              <EnvelopeSimple size={14} className="mr-1" weight={digestOptedIn ? "fill" : "duotone"} />
              {digestBusy
                ? "…"
                : digestOptedIn
                ? "Daily digest: on"
                : "Email me daily"}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={refresh}
            disabled={refreshing}
            className="rounded-full"
            data-testid="ops-refresh-btn"
          >
            <ArrowsClockwise size={14} className="mr-1" />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Link
            to="/admin/compliance"
            className="text-sm text-[#6B6B6B] hover:text-[#0E0E0E] underline underline-offset-2 whitespace-nowrap"
            data-testid="ops-compliance-link"
          >
            Compliance ledger →
          </Link>
          <Link
            to="/admin/console"
            className="text-sm text-[#6B6B6B] hover:text-[#0E0E0E] underline underline-offset-2 whitespace-nowrap"
            data-testid="ops-back-link"
          >
            Full console →
          </Link>
        </div>
      </div>

      {/* Error banner — surfaces if /ops/summary fails so the page never silently spins. */}
      {loadError && !loading && (
        <div
          className="mb-8 rounded-xl border border-[#C1272D]/40 bg-[#C1272D]/5 p-5 flex items-start gap-4"
          data-testid="ops-error-banner"
        >
          <Warning size={24} weight="duotone" className="text-[#C1272D] shrink-0" />
          <div className="flex-1">
            <p className="font-spectral text-xl tracking-tight text-[#C1272D]">
              Couldn&apos;t load operations summary
            </p>
            <p className="text-sm text-[#6B6B6B] mt-1">{loadError}</p>
          </div>
          <Button onClick={refresh} variant="outline" className="rounded-full shrink-0">
            Retry
          </Button>
        </div>
      )}

      {/* Browser-notification opt-in — one-time banner.
          Shows only when the browser supports Notifications, permission
          hasn't been granted or denied yet, AND the admin hasn't dismissed
          it in this session. Once granted, the AdminModeSwitcher fires
          a native ping the moment critical-count rises from 0 to 1+. */}
      <BrowserNotificationOptIn />

      {/* Revenue banner — the money headline, always first. Taps through
          to /admin/payments where the full month-by-month tile lives. */}
      <RevenueBanner navigate={navigate} />

      <WhatsNewPanel navigate={navigate} />

      {/* Top status banner */}
      {!loading && !loadError && (
        <div
          className={`mb-8 rounded-xl border p-5 flex items-start gap-4 ${
            criticalCount > 0
              ? "border-[#C1272D]/40 bg-[#C1272D]/5"
              : warnCount > 0
              ? "border-[#8A6A00]/40 bg-[#FFF8E6]"
              : "border-emerald-200 bg-emerald-50"
          }`}
          data-testid="ops-status-banner"
        >
          {allClear ? (
            <CheckCircle size={24} weight="duotone" className="text-emerald-700 shrink-0" />
          ) : (
            <Warning size={24} weight="duotone" className={criticalCount ? "text-[#C1272D] shrink-0" : "text-[#8A6A00] shrink-0"} />
          )}
          <div className="flex-1">
            <p className="font-spectral text-xl tracking-tight">
              {allClear
                ? "All systems healthy"
                : criticalCount > 0
                ? `${criticalCount} critical signal${criticalCount === 1 ? "" : "s"}${warnCount ? ` · ${warnCount} warning${warnCount === 1 ? "" : "s"}` : ""}`
                : `${warnCount} thing${warnCount === 1 ? "" : "s"} to review`}
            </p>
            <p className="text-sm text-[#6B6B6B] mt-1">
              {allClear
                ? "Nothing needs your attention right now. Check back tomorrow."
                : "Tap any card below to jump into the relevant page."}
              {" "}
              Last refreshed {data?.generated_at ? formatDate(data.generated_at) : "—"}.
            </p>
          </div>
        </div>
      )}

      {/* Cards grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(9)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card) => (
            <OpCard key={card.key} card={card} onNavigate={navigate} />
          ))}
        </div>
      )}

      {/* Footer — last nightly reconcile */}
      {data?.last_nightly_reconcile && (
        <div className="mt-10 text-xs text-[#6B6B6B] flex items-center gap-2" data-testid="ops-last-reconcile">
          <Lightning size={12} />
          <span>
            Nightly reconciler last ran {formatDate(data.last_nightly_reconcile.created_at)} —
            scanned {data.last_nightly_reconcile.scanned || 0}, rescued{" "}
            {data.last_nightly_reconcile.rescued_count || 0}.
          </span>
        </div>
      )}

      {/* Automation health — manual triggers + last-run signals */}
      {data?.automation?.length > 0 && (
        <AutomationHealth automation={data.automation} />
      )}

      {/* Email deliverability health — surfaces unverified-domain warning */}
      <EmailHealthPanel />

      {/* Sentry health snapshot — admin-only tile that surfaces open
          issue count + last event timestamp without context-switching
          to sentry.io. Hides silently when Sentry isn't configured. */}
      <SentryHealthTile />

      {/* Deploy tracker — "what changed since last deploy?" */}
      {deploy && (
        <DeployPanel
          deploy={deploy}
          onMarkDeployed={markDeployed}
          busy={markingDeployed}
        />
      )}

      {/* Cmd+K hint */}
      <div className="mt-12 rounded-xl border border-[#E5E5E5] bg-white p-5 flex items-center gap-4">
        <MagnifyingGlass size={20} className="text-[#6B6B6B] shrink-0" />
        <div className="flex-1">
          <p className="text-sm">
            <span className="font-semibold">Press </span>
            <kbd className="rounded border border-[#E5E5E5] bg-[#FAFAFA] px-2 py-0.5 text-xs font-mono">⌘</kbd>{" "}
            +{" "}
            <kbd className="rounded border border-[#E5E5E5] bg-[#FAFAFA] px-2 py-0.5 text-xs font-mono">K</kbd>{" "}
            anywhere in admin to search users, dogs, payments, and kennels.
          </p>
        </div>
      </div>
    </AppShell>
  );
}

function WhatsNewPanel({ navigate }) {
  const latest = ADMIN_WHATS_NEW[0]?.date || "";
  const [open, setOpen] = React.useState(() => {
    try { return localStorage.getItem("admin_whats_new_seen") !== latest; } catch { return true; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { if (!next) localStorage.setItem("admin_whats_new_seen", latest); } catch { /* ignore */ }
  };
  const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const isFresh = latest && (Date.now() - new Date(`${latest}T00:00:00Z`).getTime()) < 14 * 86400000;
  return (
    <div className="mb-8 rounded-xl border border-[#E2DAC2] bg-[#FBF7EC] overflow-hidden" data-testid="whats-new-panel">
      <button type="button" onClick={toggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-[#F5EDD8] transition-colors"
        data-testid="whats-new-toggle">
        <span className="inline-flex items-center gap-2.5">
          <Sparkle size={16} weight="duotone" className="text-gold-dark" />
          <span className="font-semibold text-[13.5px] text-ink">Recently added for you</span>
          {isFresh && (
            <span className="rounded-full bg-crimson text-white text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5">New</span>
          )}
        </span>
        <span className="text-[12px] text-[#8A6A00]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {ADMIN_WHATS_NEW.slice(0, 6).map((item, i) => (
            <button key={`${item.title}-${i}`} type="button" onClick={() => navigate(item.link)}
              className="text-left rounded-lg bg-white border border-[#EFE7CF] p-3.5 hover:border-gold transition-colors"
              data-testid={`whats-new-item-${i}`}>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A6A00]">{fmt(item.date)}</p>
              <p className="font-semibold text-[13.5px] text-ink mt-0.5">{item.title}</p>
              <p className="text-[12px] text-[#6B6B6B] mt-1 leading-relaxed">{item.blurb}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RevenueBanner({ navigate }) {
  const [rev, setRev] = React.useState(null);
  React.useEffect(() => {
    api.get("/admin/payments/revenue-summary", { silent: true })
      .then((r) => setRev(r.data))
      .catch(() => setRev(null));
  }, []);
  if (!rev) return null;
  const monthLabel = new Date(`${rev.month}-15T00:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  return (
    <button
      type="button"
      onClick={() => navigate("/admin/payments")}
      className="w-full text-left mb-8 rounded-xl bg-ink text-white border border-gold/40 p-5 flex items-center justify-between gap-4 hover:border-gold transition-colors shadow-[0_8px_30px_rgba(0,0,0,0.15)]"
      data-testid="ops-revenue-banner"
    >
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Your revenue · {monthLabel}</p>
        <p className="font-spectral text-4xl tracking-tight mt-1" data-testid="ops-revenue-total">
          ${Number(rev.total || 0).toFixed(2)}
        </p>
        <p className="text-[12px] text-white/60 mt-1">
          {(rev.sources || []).filter((s) => s.amount > 0).map((s) => `${s.label} $${s.amount.toFixed(2)}`).join(" · ") || "No revenue yet this month"}
        </p>
      </div>
      <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-white/25 px-4 py-2 text-[12.5px] font-semibold text-white/90">
        Full breakdown <ArrowRight size={13} />
      </span>
    </button>
  );
}

function OpCard({ card, onNavigate }) {
  const s = SEVERITY_STYLES[card.severity] || SEVERITY_STYLES.info;
  const Icon = s.icon;
  const displayValue =
    card.key === "revenue_24h" && typeof card.money_value === "number"
      ? `$${card.money_value.toFixed(2)}`
      : card.count;

  return (
    <button
      type="button"
      onClick={() => onNavigate(card.link)}
      className={`text-left rounded-xl border p-5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-shadow ${s.border} ${s.bg}`}
      data-testid={`ops-card-${card.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon size={16} weight="duotone" className={s.text} />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#6B6B6B]">
            {card.label}
          </p>
        </div>
        <ArrowRight size={14} className="text-[#6B6B6B]" />
      </div>
      <p className={`font-spectral text-4xl tracking-tight mt-3 ${s.text}`}>
        {displayValue}
      </p>
      <p className="text-xs text-[#6B6B6B] mt-2 leading-relaxed">{card.hint}</p>
      {card.action_label && card.severity !== "ok" && (
        <p className="text-xs font-semibold text-[#0E0E0E] mt-3 underline underline-offset-2">
          {card.action_label} →
        </p>
      )}
    </button>
  );
}


function DeployPanel({ deploy, onMarkDeployed, busy }) {
  const count = deploy?.commits_since_last_deploy || 0;
  const commits = deploy?.commits || [];
  const lastDeploy = deploy?.last_deploy;
  const isCurrent = count === 0;

  return (
    <div
      className={`mt-12 rounded-xl border p-5 ${
        isCurrent
          ? "border-emerald-200 bg-emerald-50"
          : "border-[#C9A227]/40 bg-[#FFF8E5]"
      }`}
      data-testid="deploy-panel"
    >
      <div className="flex items-start gap-4">
        {isCurrent ? (
          <CheckCircle size={22} weight="duotone" className="text-emerald-700 shrink-0 mt-0.5" />
        ) : (
          <CloudArrowUp size={22} weight="duotone" className="text-[#8A6A00] shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-spectral text-xl tracking-tight">
            {isCurrent
              ? "Production is up to date"
              : `${count} commit${count === 1 ? "" : "s"} pending redeploy`}
          </p>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {isCurrent
              ? lastDeploy
                ? `Last marked deployed ${formatDate(lastDeploy.marked_at)} · ${lastDeploy.short_sha}`
                : "No commits since the last 'just deployed' click."
              : "Save to GitHub → redeploy production → then click 'I just deployed' below."}
          </p>

          {commits.length > 0 && (
            <ol className="mt-4 space-y-2" data-testid="deploy-commit-list">
              {commits.slice(0, count > 0 ? count : 5).map((c) => (
                <li
                  key={c.sha}
                  className="flex items-start gap-3 text-[12px] leading-relaxed"
                  data-testid={`deploy-commit-${c.short_sha}`}
                >
                  <GitCommit size={13} className="text-[#6B6B6B] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <span className="font-mono text-[11px] text-[#6B6B6B]">{c.short_sha}</span>
                    <span className="text-[#6B6B6B] mx-2">·</span>
                    <span className="text-[#6B6B6B]">{formatDate(c.timestamp)}</span>
                    {c.files_human?.length > 0 && (
                      <div className="text-[#0E0E0E] mt-0.5 truncate">
                        {c.files_human.slice(0, 4).join(" · ")}
                        {c.files_human.length > 4 && (
                          <span className="text-[#6B6B6B]"> · +{c.files_human.length - 4} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
        {!isCurrent && (
          <Button
            onClick={onMarkDeployed}
            disabled={busy}
            className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full shrink-0"
            data-testid="mark-deployed-btn"
            title="Click after you've finished redeploying production. Resets the counter."
          >
            <CloudArrowUp size={14} className="mr-1" />
            {busy ? "Marking…" : "I just deployed"}
          </Button>
        )}
      </div>
    </div>
  );
}

function BrowserNotificationOptIn() {
  const [perm, setPerm] = React.useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  );
  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem("ops_notif_optin_dismissed") === "1",
  );

  if (perm !== "default") return null; // already granted, denied, or unsupported
  if (dismissed) return null;

  const enable = async () => {
    try {
      const p = await Notification.requestPermission();
      setPerm(p);
      if (p === "granted") {
        toast.success("Browser alerts on. You'll get pinged when critical signals appear.");
        // Fire a quick confirmation ping so admin can verify it works.
        try {
          new Notification("Armstrong K9 — alerts enabled", {
            body: "We'll ping you here when an operations issue needs your attention.",
            tag: "ops-optin-confirm",
          });
        } catch {
          // ignored
        }
      } else if (p === "denied") {
        toast.info("Got it — we won't ask again. You can re-enable in your browser settings.");
      }
    } catch {
      // ignored
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("ops_notif_optin_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-[#C9A227]/40 bg-[#FFF8E5] p-4 flex items-start gap-3"
      data-testid="ops-notif-optin"
    >
      <Lightning size={18} weight="duotone" className="text-[#8A6A00] shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-spectral text-base tracking-tight">Get pinged when something breaks</p>
        <p className="text-[12.5px] text-[#6B6B6B] mt-1">
          Allow browser alerts and we&apos;ll notify you the moment a paying customer hits a
          critical issue — even when this tab is in the background.
        </p>
      </div>
      <Button
        onClick={enable}
        className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full shrink-0"
        data-testid="ops-notif-enable"
      >
        Enable
      </Button>
      <button
        type="button"
        onClick={dismiss}
        className="text-[12px] text-[#6B6B6B] hover:text-[#0E0E0E] underline underline-offset-2 shrink-0 self-center"
        data-testid="ops-notif-dismiss"
      >
        Not now
      </button>
    </div>
  );
}


/**
 * AutomationHealth
 *
 * Lists every scheduled background job alongside a "Run now" button.
 * `last_run_at` is shown when we have a real source for it; otherwise
 * we display "Click to run" rather than fabricate a timestamp. After
 * a manual run, the button shows the returned summary inline so the
 * operator can confirm what happened without leaving the page.
 */
function AutomationHealth({ automation }) {
  const [runningKey, setRunningKey] = useState(null);
  const [results, setResults] = useState({}); // name → result text

  const runNow = async (name) => {
    setRunningKey(name);
    try {
      const { data } = await api.post(`/admin/automations/${name}/run-now`);
      // Summarize the most useful counters compactly. Each automation
      // returns its own shape; we surface the keys that exist and
      // skip the ones that don't.
      const parts = [];
      if (data.sent != null) parts.push(`${data.sent} sent`);
      if (data.skipped != null) parts.push(`${data.skipped} skipped`);
      if (data.rescued_count != null) parts.push(`${data.rescued_count} rescued`);
      if (data.scanned != null) parts.push(`${data.scanned} scanned`);
      if (data.released != null) parts.push(`${data.released} released`);
      if (data.archived != null) parts.push(`${data.archived} archived`);
      const summary = parts.length ? parts.join(" · ") : (data.skipped ? "Skipped (already ran)" : "Ran successfully");
      setResults((prev) => ({ ...prev, [name]: { ok: true, text: summary, at: new Date().toISOString() } }));
      toast.success(`${name} → ${summary}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Run failed";
      setResults((prev) => ({ ...prev, [name]: { ok: false, text: msg, at: new Date().toISOString() } }));
      toast.error(`${name} → ${msg}`);
    } finally {
      setRunningKey(null);
    }
  };

  return (
    <section className="mt-10" data-testid="automation-health">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
          <ArrowsClockwise size={18} weight="duotone" className="text-ink" />
          Automation health
        </h2>
        <span className="text-[12px] text-[#6B6B6B]">
          Manual triggers for scheduled jobs. Idempotent — running twice is safe.
        </span>
      </div>
      <div className="rounded-2xl border border-[#E5E5E5] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] divide-y divide-[#F0F0F0]">
        {automation.map((job) => {
          const result = results[job.name];
          const lastSeen = result?.at || job.last_run_at;
          return (
            <div
              key={job.name}
              className="flex items-center gap-3 px-4 py-3 flex-wrap"
              data-testid={`automation-row-${job.name}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-ink truncate">{job.label}</p>
                <p className="text-[11px] text-[#6B6B6B] mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span>{job.cadence}</span>
                  {lastSeen && (
                    <>
                      <span className="opacity-40">·</span>
                      <span>Last seen {formatDate(lastSeen)}</span>
                    </>
                  )}
                  {result && (
                    <>
                      <span className="opacity-40">·</span>
                      <span className={result.ok ? "text-emerald-700" : "text-[#C1272D]"}>
                        {result.text}
                      </span>
                    </>
                  )}
                  {job.hint && (
                    <>
                      <span className="opacity-40">·</span>
                      <span className="italic">{job.hint}</span>
                    </>
                  )}
                </p>
              </div>
              {job.runnable === false ? (
                <span className="text-[11px] text-[#9CA3AF] italic flex-shrink-0">
                  Auto only
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full text-[11px] h-8 px-3 flex-shrink-0"
                  disabled={runningKey === job.name}
                  onClick={() => runNow(job.name)}
                  data-testid={`automation-run-${job.name}`}
                >
                  {runningKey === job.name ? "Running…" : "Run now"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}


/**
 * EmailHealthPanel — surfaces RESEND_API_KEY + sender-domain
 * verification state so the operator can fix deliverability before
 * users start complaining about emails landing in spam.
 *
 * Hits `/api/admin/health/email` which is config introspection only
 * (no Resend round-trip), so this is fast + safe to mount on every
 * Operations page load.
 */
function EmailHealthPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/health/email")
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, []);
  if (!data) return null;
  if (data.error) return null;

  const isHealthy = data.healthy;
  return (
    <section className="mt-10" data-testid="email-health-panel">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
          <Lightning size={18} weight="duotone" className="text-ink" />
          Email deliverability
        </h2>
        <span
          className={`text-[11px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${
            isHealthy ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"
          }`}
        >
          {isHealthy ? "Verified · production-ready" : "Action required"}
        </span>
      </div>
      <div className="rounded-2xl border border-[#E5E5E5] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-5">
        <div className="grid sm:grid-cols-3 gap-4 text-[12.5px] mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-1">API key</p>
            <p className="text-ink font-semibold">{data.api_key_present ? "Configured" : "Missing"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-1">Sender</p>
            <p className="text-ink font-semibold truncate" title={data.sender_email}>{data.sender_email}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-1">Domain status</p>
            <p className={`font-semibold ${data.sender_is_verified_domain ? "text-emerald-700" : "text-amber-700"}`}>
              {data.sender_is_verified_domain ? "Custom domain in use" : "Resend test domain"}
            </p>
          </div>
        </div>
        {data.issues.length === 0 ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-[12.5px] text-emerald-800">
            All signals green. Production emails will deliver with proper DKIM/SPF.
          </div>
        ) : (
          <ul className="space-y-3">
            {data.issues.map((iss) => (
              <li
                key={iss.code}
                className={`rounded-lg border px-4 py-3 text-[12.5px] ${
                  iss.level === "critical"
                    ? "bg-rose-50 border-rose-100 text-rose-900"
                    : "bg-amber-50 border-amber-100 text-amber-900"
                }`}
                data-testid={`email-health-issue-${iss.code}`}
              >
                <p className="font-semibold mb-1">{iss.message}</p>
                <p className="text-[12px] opacity-90 leading-relaxed">{iss.fix}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}


